import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { patentService } from '../../services/patentService';

export default function AnalyzeResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const resultId = searchParams.get('result_id');

  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState('rewritten');

  const getOfficialPatentId = (patentId) => {
    return patentId.split('-')[0];
  };

  useEffect(() => {
    if (!resultId) {
      navigate('/analyze');
      return;
    }

    const fetchResult = async () => {
      try {
        setLoading(true);
        const data = await patentService.getReviewResult(resultId);
        setResultData(data);
      } catch (err) {
        setError(err.message || 'Failed to fetch analysis results');
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [resultId, navigate]);

  if (!resultId) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen px-4 py-8 flex items-center justify-center">
        <div className="text-center text-white space-y-4">
          <div className="w-16 h-16 mx-auto">
            <svg className="animate-spin text-purple-500" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
          <p className="text-lg">Loading analysis results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full bg-red-500/90 text-white rounded-2xl p-8 space-y-4 text-center">
          <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-lg font-semibold">{error}</p>
          <button
            onClick={() => navigate('/analyze')}
            className="px-6 py-2 bg-white text-red-600 rounded-xl font-semibold hover:bg-gray-100 transition-all"
          >
            Back to Analyze
          </button>
        </div>
      </div>
    );
  }

  const product = resultData?.product || {};
  const highRiskClaims = resultData?.high_risk_claims || [];
  const rewrittenProduct = resultData?.rewritten_product || {};
  const riskAssessment = resultData?.risk_assessment || {};

  const groupedPatents = {};
  highRiskClaims.forEach(claim => {
    if (!groupedPatents[claim.patent_id]) {
      groupedPatents[claim.patent_id] = {
        patent_id: claim.patent_id,
        patent_title: claim.patent_title,
        assignee: claim.assignee,
        claims: []
      };
    }
    groupedPatents[claim.patent_id].claims.push(claim);
  });
  const patents = Object.values(groupedPatents);

  return (
    <div className="min-h-screen px-4 py-8">
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
      `}</style>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/analyze')}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
          
          <h1 className="text-2xl font-bold text-white">
            Analysis Results
          </h1>
          
          <div className="w-20"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 h-fit">
            <h2 className="text-xl font-bold text-white mb-4">Product Information</h2>
            
            <div className="space-y-4">
              {product.images && product.images.length > 0 && (
                <div className="relative">
                  <img 
                    src={product.images[0]} 
                    alt={product.title}
                    className="w-full rounded-lg border border-white/20 object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}

              {product.title && (
                <div>
                  <p className="text-gray-400 text-sm font-semibold mb-1">Product Title</p>
                  <p className="text-white text-sm leading-relaxed">{product.title}</p>
                </div>
              )}

              {product.asin && (
                <div>
                  <p className="text-gray-400 text-sm font-semibold">ASIN</p>
                  <p className="text-white font-mono">{product.asin}</p>
                </div>
              )}

              {product.price && (
                <div>
                  <p className="text-gray-400 text-sm font-semibold">Price</p>
                  <p className="text-white text-lg font-bold">{product.price.raw}</p>
                </div>
              )}

              {product.description && (
                <div>
                  <p className="text-gray-400 text-sm font-semibold mb-1">Description</p>
                  <p className="text-white text-sm leading-relaxed">{product.description}</p>
                </div>
              )}

              {product.feature_bullets && product.feature_bullets.length > 0 && (
                <div>
                  <p className="text-gray-400 text-sm font-semibold mb-2">Key Features</p>
                  <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar pr-2">
                    {product.feature_bullets.map((feature, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <span className="text-purple-400 mt-1 flex-shrink-0">•</span>
                        <p className="text-white text-xs flex-1">{feature}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h2 className="text-xl font-bold text-white mb-4">Risk Assessment Summary</h2>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <p className="text-gray-400 text-sm mb-1">Total Reviewed</p>
                  <p className="text-white text-3xl font-bold">{riskAssessment.total_reviewed || 0}</p>
                  <p className="text-gray-400 text-xs">Patents</p>
                </div>
                
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <p className="text-gray-400 text-sm mb-1">High Risk Patents</p>
                  <p className="text-red-400 text-3xl font-bold">{riskAssessment.patents_with_high_risk || 0}</p>
                  <p className="text-gray-400 text-xs">Patents</p>
                </div>
                
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <p className="text-gray-400 text-sm mb-1">Risk Claims</p>
                  <p className="text-yellow-400 text-3xl font-bold">{riskAssessment.total_high_medium_risk_claims || 0}</p>
                  <p className="text-gray-400 text-xs">Claims</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Patent Search Results & Reviews</h2>
                <span className="text-gray-400 text-sm">{patents.length} patents with risk claims</span>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-4">
                {patents.slice(0, 8).map((patent, index) => (
                  <button
                    key={index}
                    onClick={() => navigate(`/patent?id=${encodeURIComponent(getOfficialPatentId(patent.patent_id))}&return=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                    className="aspect-square bg-white/5 hover:bg-white/10 rounded-lg border border-white/20 hover:border-purple-400 transition-all p-3 flex flex-col items-center justify-center text-center group"
                  >
                    <svg className="w-8 h-8 text-gray-400 group-hover:text-purple-400 transition-colors mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-white text-xs font-mono line-clamp-2">{patent.patent_id}</p>
                    <p className="text-gray-400 text-xs mt-1">{patent.claims.length} claims</p>
                  </button>
                ))}
                
                {patents.length > 8 && (
                  <div className="aspect-square bg-white/5 rounded-lg border border-white/20 p-3 flex flex-col items-center justify-center text-center">
                    <p className="text-white text-2xl font-bold">+{patents.length - 8}</p>
                    <p className="text-gray-400 text-xs mt-1">more patents</p>
                  </div>
                )}
              </div>

              {patents.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-400">No patents with risk claims found</p>
                </div>
              )}
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-center gap-4 mb-4">
                <button
                  onClick={() => setSelectedTab('rewritten')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    selectedTab === 'rewritten'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  Rewritten Product
                </button>
                <button
                  onClick={() => setSelectedTab('claims')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    selectedTab === 'claims'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  Risk Claims Details
                </button>
              </div>

              {selectedTab === 'rewritten' && (
                <div className="space-y-4">
                  {rewrittenProduct.title && (
                    <div>
                      <p className="text-gray-400 text-sm font-semibold mb-2">Rewritten Title</p>
                      <p className="text-white text-lg font-semibold leading-relaxed">{rewrittenProduct.title}</p>
                    </div>
                  )}

                  {rewrittenProduct.description && (
                    <div>
                      <p className="text-gray-400 text-sm font-semibold mb-2">Rewritten Description</p>
                      <p className="text-white text-sm leading-relaxed">{rewrittenProduct.description}</p>
                    </div>
                  )}

                  {rewrittenProduct.feature_bullets && rewrittenProduct.feature_bullets.length > 0 && (
                    <div>
                      <p className="text-gray-400 text-sm font-semibold mb-2">Rewritten Features</p>
                      <div className="bg-white/5 rounded-lg p-4 border border-white/10 max-h-96 overflow-y-auto custom-scrollbar">
                        <div className="space-y-3">
                          {rewrittenProduct.feature_bullets.map((feature, index) => (
                            <div key={index} className="flex items-start gap-2">
                              <span className="text-green-400 mt-1 flex-shrink-0">•</span>
                              <p className="text-white text-xs flex-1 leading-relaxed">{feature}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {rewrittenProduct.risk_reduction_explanation && (
                    <div>
                      <p className="text-gray-400 text-sm font-semibold mb-2">Risk Reduction Explanation</p>
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                        <p className="text-white text-sm leading-relaxed">{rewrittenProduct.risk_reduction_explanation}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedTab === 'claims' && (
                <div className="space-y-4 max-h-[600px] overflow-y-auto custom-scrollbar pr-2">
                  {patents.map((patent, pIdx) => (
                    <div key={pIdx} className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1">
                          <p className="text-white font-semibold mb-1">{patent.patent_title}</p>
                          <p className="text-gray-400 text-sm font-mono">{patent.patent_id}</p>
                        </div>
                        <button
                          onClick={() => navigate(`/patent?id=${encodeURIComponent(getOfficialPatentId(patent.patent_id))}&return=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                          className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-lg text-xs font-semibold transition-all"
                        >
                          View Details
                        </button>
                      </div>
                      
                      <div className="space-y-2">
                        {patent.claims.map((claim, cIdx) => (
                          <div key={cIdx} className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-purple-400 text-xs font-semibold">Claim {claim.claim_number}</span>
                              <span className={`text-xs font-bold px-2 py-1 rounded ${
                                claim.risk_level === 'HIGH' 
                                  ? 'bg-red-500/20 text-red-400' 
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {claim.risk_level}
                              </span>
                            </div>
                            <p className="text-white text-xs line-clamp-2 mb-2">{claim.claim_text}</p>
                            {claim.reasoning && (
                              <p className="text-gray-300 text-xs italic">{claim.reasoning}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  
                  {patents.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-400">No risk claims found</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

